import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { noop } from 'rxjs';

import { ISearchParameters } from '../../pages/page-components/Parameters/search-parameters/searchParameters';
import { EnvironmentVariablesService } from '../environment-variables.service';
import { UserService } from '../user/user.service';
import { Logging } from '../../utils/logging';
import { environment } from '../../../environments/environment';

declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  eventArgs: { [k: string]: any };
  searchParameters: any = {};
  relations: searchParamsArgs[];
  userGuid: string = '';
  userRole: string = '';
  emailAddress: string = '';
  permissions: any = null;
  private isInitialized: boolean = false;

  constructor(
    user: UserService,
    public ev: EnvironmentVariablesService,
  ) {
    this.relations = [
      { param: 'Pim', arg: 'pim' },
      { param: 'Name', arg: 'vendorName' },
      { param: 'ContrNum', arg: 'contractNumber' },
      { param: 'Desc', arg: 'vendorProductDescription' },
      { param: 'ItemType', arg: 'itemType' },
      { param: 'PartNumber', arg: 'vendorPartNumber' },
      { param: 'Psc', arg: 'PSC' },
      { param: 'SyncDesc', arg: 'syncSubcategoryDescription' },
      { param: 'SyncGroupDesc', arg: 'syncGroupDescription' },
      { param: 'UNSPSCCode', arg: 'UNSPSCCode' },
      { param: 'UNSPSCDesc', arg: 'UNSPSCCodeDescription' },
    ];

    user.userGuid.subscribe(v => {
      this.userGuid = v;
    });
    user.roles.subscribe(v => {
      this.userRole = v[0];
    });
  }

  public getUrl(): string {
    return (
      'https://www.googletagmanager.com/gtag/js?id=' +
      environment.gaMeasurementId
    );
  }

  public getEnvironment(): string {
    return "gtag('config', '" + environment.gaMeasurementId + "');";
  }

  public initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isInitialized) {
        Logging.warn('Google Analytics already initialized');
        resolve();
        return;
      }

      // Check if GA measurement ID is configured
      if (!environment.gaMeasurementId) {
        Logging.warn(
          'Google Analytics measurement ID not configured for this environment',
        );
        (window as any).gtag = noop;
        resolve();
        return;
      }

      try {
        // Create dataLayer and gtag function
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).gtag = function () {
          (window as any).dataLayer.push(arguments);
        };

        // Set the current time
        (window as any).gtag('js', new Date());

        // Load gtag.js script
        const script = document.createElement('script');
        script.async = true;
        script.src = this.getUrl();
        script.onload = () => {
          (window as any).gtag('config', environment.gaMeasurementId);
          this.isInitialized = true;
          Logging.info(
            'Google Analytics initialized successfully with ID:',
            environment.gaMeasurementId,
          );
          resolve();
        };
        script.onerror = error => {
          Logging.error('Failed to load Google Analytics script', error);
          reject(error);
        };

        document.head.appendChild(script);
      } catch (error) {
        Logging.error('Error initializing Google Analytics', error);
        reject(error);
      }
    });
  }

  public exportEvent(pageName: string, exportType: string): void {
    //Reset event args before gtag call
    this.eventArgs = {};

    this.basicArguments();
    this.eventArgs['exportType'] = exportType;

    switch (pageName) {
      case 'ODSearch': {
        gtag('event', 'Search Export Clicked', this.eventArgs);
        break;
      }
      case 'LIU': {
        gtag('event', 'LIU Export Clicked', this.eventArgs);
        break;
      }
      default: {
        break;
      }
    }
  }

  public searchDurationInfo(time: string, recordCount: number, status: string) {
    this.eventArgs = {};

    this.basicArguments();
    this.eventArgs['searchDuration'] = time;
    this.eventArgs['searchRecordCount'] = recordCount;
    this.eventArgs['searchStatus'] = status; //Error | NoRecords | Success

    gtag('event', 'Search Duration Info', this.eventArgs);
  }

  public cccPullCrossesEvent(parameters: any): void {
    //Reset event args before gtag call
    this.eventArgs = {};
    this.basicArguments();
    this.addCCCArgs(parameters);
    gtag('event', 'CCC PullCrosses Started', this.eventArgs);
  }

  private addCCCArgs(parameters: any): void {
    if (parameters.UnspscCode !== null && parameters.UnspscCode.length > 0) {
      this.eventArgs['isUnspscSelected'] = true;
    }
    if (
      parameters.ProductSpendCategory !== null &&
      parameters.ProductSpendCategory.length > 0
    ) {
      this.eventArgs['isPscSelected'] = true;
    }
    if (
      parameters.SyncSubCategory != null &&
      parameters.SyncSubCategory.length > 0
    ) {
      this.eventArgs['isSyncSelected'] = true;
    }
    if (parameters.IncludeAttribution) {
      this.eventArgs['isAttributionSelected'] = true;
    }
    if (parameters.IncludeDerived) {
      this.eventArgs['isDerivationSelected'] = true;
    }
    if (parameters.SameVendorSet) {
      this.eventArgs['isSameVendorSelected'] = true;
    }
  }

  public searchEvent(parameters: ISearchParameters): void {
    //Reset event args before gtag call
    this.eventArgs = {};

    this.searchParameters = parameters;
    this.basicArguments();
    this.addSearchArgs();

    gtag('event', 'Search Started', this.eventArgs);
  }

  public uploadEvent(): void {
    //Reset event args before gtag call
    this.eventArgs = {};

    this.basicArguments();

    gtag('event', 'Upload Started', this.eventArgs);
  }

  public tabClickEvent(tabName: string): void {
    //Reset event args before gtag call
    this.eventArgs = {};
    this.eventArgs['tabName'] = tabName;
    this.basicArguments();

    gtag('event', 'Nav Menu Clicked', this.eventArgs);
  }

  //Make sure to include this method before making a call to the gtag function
  private basicArguments(): void {
    const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';
    this.eventArgs['userName'] = this.userGuid;
    this.eventArgs['userGuid'] = this.userGuid;
    this.eventArgs['userRole'] = this.userRole;
    this.eventArgs['timeStamp'] = moment(new Date(), DATE_TIME_FORMAT).format(
      DATE_TIME_FORMAT,
    );
  }

  private addSearchArgs(): void {
    let numberofparameters = 0;

    if (this.searchParameters.VendorNames.length > 0) {
      this.eventArgs['VendorNames'] =
        this.searchParameters.VendorNames.split('').join(',');
      this.eventArgs['VendorNamesCount'] =
        this.searchParameters.VendorNames.split('').length;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.ProductKeys.length > 0) {
      this.eventArgs['ProductKeys'] = this.searchParameters.ProductKeys;
      this.eventArgs['ProductKeysCount'] =
        this.searchParameters.ProductKeys.split(',').length;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.ContractNumbers.length > 0) {
      this.eventArgs['ContractNumbers'] = this.searchParameters.ContractNumbers;
      this.eventArgs['ContractNumbersCount'] =
        this.searchParameters.ContractNumbers.split(',').length;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.VendorPartNumbers.length > 0) {
      this.eventArgs['VendorPartNumbers'] =
        this.searchParameters.VendorPartNumbers;
      this.eventArgs['VendorPartNumbersCount'] =
        this.searchParameters.VendorPartNumbers.split(',').length;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.ConfidenceLabels.length > 0) {
      this.eventArgs['ConfidenceLabels'] =
        this.searchParameters.ConfidenceLabels;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.VendorProductDescriptions.length > 0) {
      this.eventArgs['VendorProductDescriptions'] =
        this.searchParameters.VendorProductDescriptions;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.UnspscCodes.length > 0) {
      this.eventArgs['UnspscCodes'] = this.searchParameters.UnspscCodes;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.ProductSpendCatergories.length > 0) {
      this.eventArgs['ProductSpendCategories'] =
        this.searchParameters.ProductSpendCatergories;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.BatchIds.length > 0) {
      this.eventArgs['BatchIds'] = this.searchParameters.BatchIds;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.Sources.length > 0) {
      this.eventArgs['Sources'] = this.searchParameters.Sources;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.SyncGroupDescriptions.length > 0) {
      this.eventArgs['SyncGroupDescriptions'] =
        this.searchParameters.SyncGroupDescriptions;
      numberofparameters = numberofparameters + 1;
    }
    if (this.searchParameters.SyncSubcategoryDescriptions.length > 0) {
      this.eventArgs['SyncSubcategoryDescriptions'] =
        this.searchParameters.SyncSubcategoryDescriptions;
      numberofparameters = numberofparameters + 1;
    }

    this.eventArgs['numberOfParameters'] = numberofparameters;
  }
}

export class searchParamsArgs {
  param: string;
  arg: string;
}
